/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 */

import React, { useRef, useEffect, useState } from "react";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Why from "./components/Why";
import About from "./components/About";
import Collection from "./components/Collection";
import QnA from "./components/QnA";
import Footer from "./components/Footer";
import FlameEffect from "./components/FlameEffect";
import Contact from "./components/Contact";
import Blog from "./components/Blog";
import TeamModal from "./components/TeamModal";
import Hyperspace from "./components/Hyperspace";
import Realms from "./components/Realms";
import Credits from "./components/Credits";
import SEOHead from "./components/SEOHead";
import SEOAnalytics from "./components/SEOAnalytics";
import InfographicRoadmapSimple from "./components/InfographicRoadmapSimple.jsx";
import Typography from "./components/Typography.jsx";
import LightweightStarField from "./components/LightweightStarField";

function HomePage() {
  const audioRef = useRef(null);
  const [blogOpen, setBlogOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [hyperspaceOpen, setHyperspaceOpen] = useState(false);
  const [infographicOpen, setInfographicOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [spaceSoundIndex, setSpaceSoundIndex] = useState(0);

  const handleHyperspaceOpen = () => {
    setHyperspaceOpen(true);
    // Increment image index each time hyperspace is opened
    setImageIndex((prevIndex) => prevIndex + 1);
    // Cycle through space sounds (0, 1, 2)
    setSpaceSoundIndex((prevIndex) => (prevIndex + 1) % 3);
  };

  const handleHyperspaceClose = () => {
    setHyperspaceOpen(false);
  };

  useEffect(() => {
    // Ensure page starts at the top (Hero section)
    window.scrollTo(0, 0);

    // Audio initialization without autoplay - user must click play button
    const audio = audioRef.current;

    const initializeAudio = () => {
      if (!audio) return;

      // Set volume and prepare audio (but don't play automatically)
      audio.volume = 0.7;
      console.log("Audio initialized - ready for user interaction");
    };

    initializeAudio();

    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  return (
    <div
      id="top"
      style={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* Stars Background - Paused when voyage modal is open */}
      <LightweightStarField isPaused={infographicOpen} />
      {/* SEO Meta Tags Management */}
      <SEOHead page="home" />
      {/* Analytics and Tracking */}
      <SEOAnalytics />
      <div
        style={{
          position: "fixed",
          top: 20,
          left: 20,
          zIndex: 1000,
          display: "flex",
          gap: "10px",
          flexDirection: window.innerWidth < 500 ? "column" : "row",
        }}
      >
        {" "}
      </div>
      <Navbar
        audioRef={audioRef}
        onBlogClick={() => setBlogOpen(true)}
        onCreditsClick={() => setCreditsOpen(true)}
        onTeamClick={() => setTeamOpen(true)}
        onVoyageClick={() => setInfographicOpen(true)}
      />
      {/* 3D Typography Banner */}
      <Typography transparent={true} />
      <Hero id="hero" />
      <FlameEffect />
      <Why id="why" />
      <FlameEffect />
      <About id="about" />
      <FlameEffect />
      <Collection id="collection" />
      <FlameEffect />
      <Realms id="realms" />
      <FlameEffect />
      <QnA id="qna" />
      <FlameEffect /> <Contact /> {/* Contact comes after FAQ */}
      <FlameEffect />
      <Footer />
      {/* Floating Hyperspace Button */}
      <button
        className="hyperspace-button"
        onClick={handleHyperspaceOpen}
        title={imageIndex % 2 === 0 ? "Enter Hyperspace Tunnel (Ships)" : "Enter Lightspeed Tunnel"}
      >
        <img
          src={require("./images/Hyper1.png")}
          alt="Hyperspace"
          style={{
            width: "120%",
            height: "120%",
            objectFit: "contain",
            opacity: 1,
            filter: "brightness(1.2) contrast(1.1) saturate(1.3)",
          }}
        />
      </button>
      {/* Hyperspace Component */}
      <Hyperspace
        isOpen={hyperspaceOpen}
        onClose={handleHyperspaceClose}
        imageIndex={imageIndex}
        spaceSoundIndex={spaceSoundIndex}
      />
      <Blog open={blogOpen} onClose={() => setBlogOpen(false)} />
      <TeamModal open={teamOpen} onClose={() => setTeamOpen(false)} />
      <Credits open={creditsOpen} onClose={() => setCreditsOpen(false)} />
      {/* Infographic Voyage Modal */}
      {infographicOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="voyage-modal-title"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#000",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
          onClick={() => setInfographicOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setInfographicOpen(false);
          }}
          tabIndex={0}
        >
          {/* Video Background for Voyage Modal */}
          <video
            className="voyage-video-background"
            muted
            loop
            playsInline
            autoPlay
            webkit-playsinline="true"
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              minWidth: "100%",
              minHeight: "100%",
              width: "auto",
              height: "auto",
              transform: "translate(-50%, -50%)",
              zIndex: -1,
              objectFit: "cover",
              filter: "brightness(0.3) contrast(1.1)",
            }}
          >
            <source src="/videos/bg.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          <div
            className="voyage-modal-container"
            role="document"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              backgroundColor: "transparent",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            {/* Close Button */}
            <button
              style={{
                position: "fixed",
                top: "30px",
                right: "30px",
                zIndex: 10001,
                background: "rgba(218, 165, 32, 0.9)",
                border: "none",
                borderRadius: "50%",
                width: "50px",
                height: "50px",
                color: "#000",
                fontSize: "24px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.5)",
              }}
              onClick={() => setInfographicOpen(false)}
            >
              ×
            </button>

            {/* Infographic Component */}
            <InfographicRoadmapSimple />
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
